import { useContext } from "react";
import { CoreAlert, coreToast } from "@verity/components/core";
import { PortalControllerContext } from "../../../context";
import { useRegisterForLiveSessionMutation, useUnregisterForLiveSessionMutation } from "./store/hooks";
import { Assessment, LiveSession } from "./store/slices/assessments";
import i18n from "../../../../i18n";
import { filterRegisteredLiveSessions } from "./filter";
import BackBanner from "../../back-banner";
import PageCard from "../../page-card";
import LiveSessionSelectorPageContent from "../../../../live/register/LiveSessionSelector";
import styles from "./register-live-session-page.module.css";
import Assessments from "../../../../images/portal/live-schedule.svg?react";

export default function RegisterLiveSessionPage({
	assessment,
	onBack,
	onFinish,
}: {
	assessment: Assessment;
	onBack: () => void;
	onFinish: (selectedSession?: LiveSession) => void;
}) {
	const { name, liveSessions } = assessment;
	const { portalContext, userInfo } = useContext(PortalControllerContext);

	const [registerForLiveSession] = useRegisterForLiveSessionMutation();
	const [unregisterForLiveSession] = useUnregisterForLiveSessionMutation();

	const existingSessions = filterRegisteredLiveSessions(assessment.liveSessions, userInfo?.uuid);
	const currSelectedSessionId = existingSessions[0]?.id;

	return (
		<>
			<BackBanner onBack={onBack}>{name}</BackBanner>
			<div className={styles.content}>
				<CoreAlert
					severity="info"
					size="large"
					boxShadow
					title={i18n.t("portal.components.studentAssessmentPageTSX.registerLiveSessionPageTSX.requireRegistration")}
				>
					{i18n.t("portal.components.studentAssessmentPageTSX.registerLiveSessionPageTSX.chooseTimeSlot")}
				</CoreAlert>
				<PageCard
					className={styles.card}
					bodyClassName={styles.cardBody}
					icon={<Assessments />}
					title={i18n.t("live.registerTSX.registerLive")}
				>
					<LiveSessionSelectorPageContent
						sessions={liveSessions}
						initialSelectedSessionId={currSelectedSessionId}
						onSave={async (selectedSessionId) => {
							const userUuid = userInfo?.uuid;
							if (userUuid && selectedSessionId) {
								await registerForLiveSession({
									...portalContext,
									liveSessionId: selectedSessionId,
									userId: userUuid,
								}).unwrap();
								await Promise.allSettled(
									existingSessions.map(({ id }) =>
										unregisterForLiveSession({
											...portalContext,
											liveSessionId: id,
											userId: userUuid,
										}).unwrap(),
									),
								);
								coreToast.success(
									i18n.t("portal.components.studentAssessmentPageTSX.registerLiveSessionPageTSX.registerSuccess", {
										assessmentName: name,
									}),
								);
								onFinish(liveSessions.find(({ id }) => id === selectedSessionId));
							}
						}}
					/>
				</PageCard>
			</div>
		</>
	);
}
